import React, { useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import { IconArrowDown, IconMore } from 'components/Icons'

import { StaffLoading, HEADERS } from 'features/Staff'
import type { IStaff, IList, ISorting } from 'features/Staff'
import type { IColumn } from 'types'

export const StaffList: React.FC<IList> = ({
  columns = HEADERS,
  onSortChange = () => {},
  sorting = {
    column: '',
    order: 'ASC',
  },
  isEmpty = false,
  isLoading,
  isUnSelectable = false,
  render,
}: IList) => {
  const base = 'f-staff-list'
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [selecting, setSelecting] = useState<IStaff[]>([])
  const { t } = useTranslation('common')

  const iconCn = (column: IColumn): string => {
    const shouldActive =
      sorting.column === column.key && sorting.order === 'DESC'

    return clsx(`${base}__icon`, shouldActive && `${base}__icon--active`)
  }

  const setSortingHandle = (column: IColumn) => {
    if (column.key === sorting.column) {
      const toggle = sorting.order === 'ASC' ? 'DESC' : 'ASC'
      const next: ISorting = { column: column.key, order: toggle }

      return onSortChange(next)
    }

    const next: ISorting = { column: column.key, order: 'DESC' }

    return onSortChange(next)
  }

  const setSelectingHandle = (currentStaff: IStaff) => (checked: boolean) => {
    if (!checked) {
      const filtered = selecting.filter(({ id }) => id !== currentStaff.id)

      return filtered && setSelecting(filtered)
    }

    const isDuplicate = selecting.find(({ id }) => id === currentStaff.id)

    if (!isDuplicate) {
      return setSelecting([...selecting, currentStaff] as IStaff[])
    }
  }

  return (
    <table className={`${base}__list`}>
      <thead className={`${base}__list-header-con`}>
        <tr className={`${base}__list-header`}>
          {!isUnSelectable && (
            <th className={`${base}__cell--w3`}>
              <div
                className={`${base}__cell-header-wrapper`}
                onClick={() => setIsOpen(!isOpen)}
              >
                <IconMore className={`${base}__icon`} variants='sm' />
              </div>
            </th>
          )}
          {columns.map((column, index) => (
            <th className={`${base}__cell--w${column.width}`} key={index}>
              <div
                className={`${base}__cell-header-wrapper`}
                {...(column.sortable && {
                  onClick: () => setSortingHandle(column),
                })}
              >
                <span className={`${base}__cell-header-text`}>
                  {column.label}
                </span>
                {column.sortable && (
                  <IconArrowDown className={iconCn(column)} variants='xs' />
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      {isLoading ? (
        <StaffLoading />
      ) : !isEmpty ? (
        <tbody className={`${base}__list-body`}>
          {render({ isOpen, selecting, setSelectingHandle })}
        </tbody>
      ) : (
        <tbody className={`${base}__list-body ${base}__list-body--empty`}>
          <tr className={`${base}__cell-body-text`}>
            <td>{t('dataNotFound.label')}</td>
          </tr>
        </tbody>
      )}
    </table>
  )
}

export default StaffList
